import type { StateTree } from '@makemydeal/dr-dash-types';
import type { FuelSavingData } from '@makemydeal/dr-platform-types';
import type { BaggageFuelEconomyPayload } from '@makemydeal/dr-dash-bff-types';
import { calculateFuelSavings, getPeriodMultiplier } from '@makemydeal/dr-activities-fuel-saving';
import type { Period } from '@makemydeal/dr-activities-fuel-saving';
import * as tradeInsSelectors from './tradeIns';
import * as vehicleSelectors from './vehicleSelectors';
import { getBaggageFuelEconomyState } from './baggageFuelEconomySelectors';

export type FuelSavingPrintData = FuelSavingData;

type TradeEntry = NonNullable<BaggageFuelEconomyPayload['trades']>[string];

const toNumber = (value: unknown): number | undefined => {
    if (value == null || value === '') return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
};

const normalizeMilesPer = (milesPer: unknown): Period => {
    const v = String(milesPer || '').toUpperCase();
    return v === 'MONTH' || v === 'YEAR' ? (v as Period) : 'WEEK';
};

const getNewVehicleName = (state: StateTree): string =>
    [
        vehicleSelectors.getVehicleYear(state),
        vehicleSelectors.getVehicleMake(state),
        vehicleSelectors.getVehicleModel(state),
        vehicleSelectors.getVehicleTrim(state)
    ]
        .filter(Boolean)
        .join(' ')
        .trim();

const getIncludedTradeVins = (state: StateTree): Set<string> => {
    const vins = new Set<string>();
    for (const { tradeIn } of tradeInsSelectors.getIncludedTradeIns(state) ?? []) {
        const vin = tradeIn?.vin?.toUpperCase();
        if (vin) vins.add(vin);
    }
    return vins;
};

const isTradeIncluded = (rawVin: string, includedVins: Set<string>): boolean => includedVins.has(rawVin.toUpperCase());

const isTradeCalculable = (trade: TradeEntry): boolean =>
    toNumber(trade.tradeInMpg) != null && toNumber(trade.fuelPricePerGallon) != null && toNumber(trade.milesDriven) != null;

const averagedMonthlySavings = (
    yearlyMiles: number,
    tradeInMpg: number,
    tradePpg: number,
    newVehicleMpg: number,
    newVehiclePpg: number | undefined
): number => {
    const { monthlySavings } = calculateFuelSavings(
        yearlyMiles,
        'YEAR',
        newVehicleMpg,
        tradeInMpg,
        newVehiclePpg ?? tradePpg,
        tradePpg
    );
    return monthlySavings;
};

// Maps included-trade fuel baggage into the print payload. Mirrors Falcon's "Standalone Fuel
// Economy Print": averages the trade MPG, fuel price and yearly miles across qualifying trades
// first, then computes a single averaged trade-in cost vs the new vehicle (so the default print
// matches the Fuel Savings Analysis print). Returns undefined when no qualifying data exists.
// primaryState: when provided, baggage and trade-in inclusion are resolved from the primary
// scope's state (the primary scope receives all SET_BAGGAGE_FUEL_ECONOMY dispatches and
// accumulates vehicle data for all scenarios; non-primary scopes never receive this action).
export const getFuelSavingsPrintData = (state: StateTree, primaryState?: StateTree): FuelSavingPrintData | undefined => {
    const payload = getBaggageFuelEconomyState(primaryState ?? state)?.payload;
    if (!payload?.trades) return undefined;

    const vin = vehicleSelectors.getVehicleVin(state);
    const vehicle = vin ? (payload.vehicles ?? {})[vin] : undefined;
    if (!vehicle) return undefined;

    const newVehicleMpg = toNumber(vehicle.newVehicleMpg);
    if (newVehicleMpg == null) return undefined;

    const newVehiclePpg = toNumber(vehicle.fuelPricePerGallon);
    const includedVins = getIncludedTradeVins(primaryState ?? state);
    if (includedVins.size === 0) return undefined;

    const qualifyingTrades = Object.entries(payload.trades).filter(
        ([tradeVin, trade]) => trade && isTradeIncluded(tradeVin, includedVins) && isTradeCalculable(trade)
    ) as [string, TradeEntry][];

    if (qualifyingTrades.length === 0) return undefined;

    const n = qualifyingTrades.length;
    const tradeMpgAverage = qualifyingTrades.reduce((sum, [, t]) => sum + toNumber(t.tradeInMpg)!, 0) / n;
    const tradePpgAverage = qualifyingTrades.reduce((sum, [, t]) => sum + toNumber(t.fuelPricePerGallon)!, 0) / n;
    const yearlyMilesAverage =
        qualifyingTrades.reduce(
            (sum, [, t]) => sum + toNumber(t.milesDriven)! * getPeriodMultiplier(normalizeMilesPer(t.milesPer)),
            0
        ) / n;

    // Falcon approach: average trade MPG, price and yearly miles first, then compute the monthly
    // savings of one averaged trade-in against the new vehicle (instead of averaging per-trade savings).
    const monthly = averagedMonthlySavings(yearlyMilesAverage, tradeMpgAverage, tradePpgAverage, newVehicleMpg, newVehiclePpg);

    return {
        sellingVehicles: [
            {
                vehicleName: getNewVehicleName(state),
                mpg: newVehicleMpg,
                savings: { monthly }
            }
        ],
        averageTradeMpg: tradeMpgAverage
    };
};
